// ADR 11.9.26a §1 — the wasm init's two phases, and what a failure of either
// reports.
//
// NO TOP-LEVEL `await` HERE, AND NO IMPORT OF `./index`. That module awaits the
// real init at module scope, so any suite importing it runs a real
// instantiation before its first `it`. Everything a test needs to drive — the
// staged runner, the report, the refusal to render — lives here instead, and
// `__tests__/importGraph.test.ts` (AC8) is what keeps it that way.
//
// The phases are injected rather than `vi.mock`ed. `telemetryFailure.test.ts`
// records the measurement: a hoisted mock factory is evaluated once per file
// and survives `vi.resetModules()`, so two failure arms in one file would need
// `vi.doMock` plus a dynamic import per case. Two stubs that throw are two
// plain function calls.

import { capture, reportNonFatal } from '../telemetry/capture'
import { revealFatal } from '../app/revealFatal'
import { wasmInitOutcome } from './initOutcome'

/**
 * The Sentry fingerprint — a **single** element, unlike 8.9.26b's
 * `['web-telemetry-init-failed', sdk]`. Two SDKs fail for unrelated reasons and
 * are triaged by different people, so two issues is right there; both wasm
 * stages are one artefact, one runbook and one first responder, so they group
 * and `wasm.stage` separates them *inside* the issue.
 */
export const WASM_INIT_FINGERPRINT = 'wasm-init'

/** The PostHog event: "how many, and did it stop" — Sentry's is "what broke". */
export const WASM_INIT_FAILED_EVENT = 'web_wasm_init_failed'

/**
 * A stage is a phase, not an API. `fetch` means *acquiring the bytes* (`fetch`
 * in a browser, `readFile` under node) and `instantiate` means *compiling them*
 * (`init` / `initSync`), so both labels are reachable in either environment.
 *
 * The tag is the whole diagnostic value: a `fetch` failure is a CDN, a cache or
 * a corporate proxy; an `instantiate` failure is a browser too old for a
 * feature `wasm-pack` emitted, or a truncated response. Different owners.
 */
export type WasmInitStage = 'fetch' | 'instantiate'

export interface WasmInitFailure {
  stage: WasmInitStage
  reason: string
}

/**
 * The two phases, as closures. `index.ts` supplies the browser or node pair; a
 * suite supplies stubs. `now()` is injected so a test can pin the duration.
 */
export interface InitDeps<Bytes> {
  acquire(): Promise<Bytes>
  instantiate(bytes: Bytes): Promise<void> | void
  now(): number
}

/**
 * Discriminated rather than `number | WasmInitFailure`: the bare union needs a
 * `typeof` at the call site to tell a duration from a failure, and the two arms
 * are exactly what AC4 and AC5 describe.
 */
export type InitOutcome = { ok: true; ms: number } | { ok: false; failure: WasmInitFailure }

/**
 * The exception's text rides in the report *message*, never in a tag — where
 * 6.9.26o §3 refused to attach one. The input is the difference: that
 * `TypeError` comes from a `map` over user-generated plan content and can quote
 * it; this one comes from fetching a static asset at a build-time URL and
 * compiling it, so its text is a network status or a wasm section offset with
 * no user content anywhere near it.
 */
function reasonOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * Run both phases, timing the whole — the learner waits on both — and
 * **resolve** on either failure rather than throw. A rejection here would
 * reject `index.ts`'s top-level `await`, and with it every importer and the
 * `import('./wasm')` `main.tsx` refuses and reports on (ADR 25.9.26f §4).
 */
export async function runInit<Bytes>(deps: InitDeps<Bytes>): Promise<InitOutcome> {
  const started = deps.now()
  let bytes: Bytes
  try {
    bytes = await deps.acquire()
  } catch (error) {
    return { ok: false, failure: { stage: 'fetch', reason: reasonOf(error) } }
  }
  try {
    await deps.instantiate(bytes)
  } catch (error) {
    return { ok: false, failure: { stage: 'instantiate', reason: reasonOf(error) } }
  }
  return { ok: true, ms: Math.round(deps.now() - started) }
}

/**
 * The two reports are deliberate, not redundant. Sentry answers "what broke,
 * where"; PostHog answers "how many, and did it stop" — and 6.9.26c's consent
 * gate means the PostHog one is absent for a learner who declined, which is
 * precisely why the Sentry one is not the same signal.
 *
 * Called through `reportRecordedFailure` once `initTelemetry` has settled,
 * never from `index.ts`: the init can finish before either SDK handle exists,
 * so a report from its catch could meet two null handles and vanish.
 */
export function reportWasmInitFailure(failure: WasmInitFailure): void {
  reportNonFatal(`wasm init failed: ${failure.reason}`, [WASM_INIT_FINGERPRINT], {
    'wasm.stage': failure.stage,
  })
  capture(WASM_INIT_FAILED_EVENT, { stage: failure.stage })
}

/**
 * Resolving is not swallowing. An app rendering over a facade whose every call
 * throws is worse than a blank page, so the enforcement moves from the throw to
 * a refusal to render — the only version that leaves anyone alive to file the
 * report. Reveals the **existing** `<template id="fatal">` `index.html` ships;
 * no wasm-specific copy, no new string (9.9.26t's deferral, kept).
 *
 * Returns whether the render must stop.
 *
 * ADR 25.9.26f §4 — React now renders before the wasm has arrived, so the
 * refusal usually lands over a live root. `revealFatal` empties `#root`, and a
 * still-mounted React would later commit into detached nodes, so `root` is
 * unmounted first. It is taken structurally, which keeps this module out of
 * the top-level-`await` graph (11.9.26a AC8); the no-root calls — the static
 * shell's and the error boundary's rows — keep their meaning.
 */
export function refuseRender(
  failure: WasmInitFailure | null,
  root?: { unmount(): void },
): boolean {
  if (failure === null) return false
  root?.unmount()
  revealFatal()
  return true
}

/**
 * ADR 25.9.26f §4 — the one report of a recorded init failure, once both the
 * telemetry chain and the facade import have settled, **in either order**.
 *
 * It used to be a `.then` on the telemetry chain, which could read the slot
 * early because a static import guaranteed the facade had evaluated. Without
 * that import the chain can settle first, read `null` and report nothing —
 * 11.9.26a's silent-failure class — so the wait is on both. A rejected facade
 * import is `main.tsx`'s `reportFatal`, never a second report here, and
 * `allSettled` leaves no rejection unhandled.
 *
 * ADR 16.10.26p §3 — the failure comes from `outcome`, not the facade's
 * namespace: Safari can resolve the import before `index.ts`'s body has run,
 * and a slot read then threw. The outcome is awaited only after the facade
 * resolved, since a rejected import never settles it.
 */
export async function reportRecordedFailure(
  telemetry: Promise<unknown>,
  facade: Promise<unknown>,
  outcome: Promise<WasmInitFailure | null> = wasmInitOutcome,
): Promise<void> {
  const [, loaded] = await Promise.allSettled([telemetry, facade])
  if (loaded.status === 'rejected') return
  const failure = await outcome
  if (failure !== null) reportWasmInitFailure(failure)
}
